const storagePrefix = "estate-platform:recent:";
const recentLimit = 8;
const idPattern = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/v;

export interface RecentScope {
  readonly userId: string;
  readonly householdId: string;
}

const storageKey = ({ userId, householdId }: RecentScope): string =>
  `${storagePrefix}${userId}:${householdId}`;

const parsed = (stored: string | null): readonly string[] => {
  if (stored === null) {
    return [];
  }
  const value: unknown = JSON.parse(stored);
  const items: readonly unknown[] = Array.isArray(value) ? value : [];
  return items
    .filter((id): id is string => typeof id === "string" && idPattern.test(id))
    .slice(0, recentLimit);
};

// WHY: storage may be blocked (private mode, a full quota) or hold something else; recents are a convenience and never fail a page.
export const readRecent = (scope: RecentScope): readonly string[] => {
  try {
    return parsed(localStorage.getItem(storageKey(scope)));
  } catch {
    return [];
  }
};

const write = (scope: RecentScope, ids: readonly string[]): void => {
  try {
    localStorage.setItem(storageKey(scope), JSON.stringify(ids));
  } catch {
    // WHY: a device that cannot store recents simply shows none.
  }
};

export const forgetRecent = (scope: RecentScope, ids: readonly string[]): void => {
  write(
    scope,
    readRecent(scope).filter((known) => !ids.includes(known)),
  );
};

export const rememberRecent = (scope: RecentScope, id: string): void => {
  if (!idPattern.test(id)) {
    return;
  }
  write(scope, [id, ...readRecent(scope).filter((known) => known !== id)].slice(0, recentLimit));
};

// WHY: only ids reach the disk; titles are resolved through search_records, so a device keeps no names of what was opened.
const removeRecents = (): void => {
  const stored = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index));
  for (const key of stored) {
    if (key?.startsWith(storagePrefix) === true) {
      localStorage.removeItem(key);
    }
  }
};

// WHY: typed searches can name private matters, so they stay in memory for the session and never reach the disk. They live outside the query cache, which refetches (and so would empty) every query on each reconnect.
const typedQueries = new Map<string, readonly string[]>();
const typedListeners = new Set<() => void>();
const noQueries: readonly string[] = [];

export const readRecentQueries = (householdId: string): readonly string[] =>
  typedQueries.get(householdId) ?? noQueries;

const notifyQueries = (): void => {
  for (const listener of typedListeners) {
    listener();
  }
};

export const writeRecentQueries = (householdId: string, queries: readonly string[]): void => {
  typedQueries.set(householdId, queries.slice(0, recentLimit));
  notifyQueries();
};

export const subscribeRecentQueries = (listener: () => void): (() => void) => {
  typedListeners.add(listener);
  return () => {
    typedListeners.delete(listener);
  };
};

export const clearRecents = (): void => {
  typedQueries.clear();
  notifyQueries();
  try {
    removeRecents();
  } catch {
    // WHY: storage that cannot be read holds no recents to clear.
  }
};
